import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from '@material-ui/core';
import {
    Alert,
    DropZone,
    PrimaryButton,
    Radio,
    SimplePopover,
    TertiaryButton,
    TextArea,
    Typography,
    UploadedFile,
    UploadProgress,
} from '@dev/ee-components';
import { sendEventToGTM } from '@dev/ee-tools';
import isEmail from 'validator/lib/isEmail';
import { filter } from 'lodash-es';
import { GetMailchimpListMembers, getIntegrationsState } from '@/store/integrations/actions';
import { debounce } from '@/services/tools/debounce';
import { getAccountStore } from '@/store/account/actions';
import { ShowToast } from '@/store/toast/actions';
import { GetContactsPatternStart } from '@/store/contacts/actions';
import { getCustomFieldsStore } from '@/store/customFields/actions';
import { getUploadStore, UploadFiles } from '@/store/upload/actions';
import { links } from '@/services/dictionaries/links';
import { isReseller } from '@/services/Secuirty';
import { STEPS, encodingArray, ButtonFooter } from '../..';
import { AddContactsProps, AddContactButtonFooterComponentProps, UploadContactsProps } from '../../ContactsUpload';
import WrongFileModal from '../../WrongFileModal';

const FileFormat = styled.div`
    padding-top: 28px;
    display: flex;
    align-items: flex-end;
    & > * + * {
        padding-left: 10px;
    }
`;

const InfoDiv = styled.div`
    margin-bottom: 32px;
`;

const mimeTypes = {
    zip: 'application/zip',
    csv: 'text/csv', //works for dropdown, but not file browser
    json: 'application/json',
    xml: 'text/xml',
    ' ': '.csv', //works for file browser, but not dropdown
    csv1: 'application/vnd.ms-excel', //works on windows
    csv2: 'application/csv',
    csv3: 'text/x-csv',
    csv4: 'application/x-csv',
    csv5: 'text/comma-separated-values',
    csv6: 'text/x-comma-separated-values',
    zip1: '.zip',
};

const MAX_LIST_UPLOAD_SIZE = 20_971_520; // 20mb

const TextField = (props: { setContactsPasted: (contactsPasted) => void }): JSX.Element => {
    const { t } = useTranslation();

    const handleTextFieldEdition = (event): void => {
        const data = event.target.value;
        const contacts = data.replace(/\s/g, '').split(/[,;]/);
        let pastedContacts = [];

        if (!contacts.length) {
            return;
        }

        if (contacts !== pastedContacts) {
            pastedContacts = contacts;
            const contactsPasted = pastedContacts.map((contact): { email: string } => ({
                email: contact,
            }));
            debounce((): void => {
                props.setContactsPasted(contactsPasted);
            }, 500);
        }
    };

    return (
        <div>
            <TextArea
                id="new-contacts-textarea"
                placeholder="email1, email2, ..."
                name="NewContactsTextArea"
                label={t('uploadtypecontacts')}
                disabled={false}
                scalable={false}
                rows={5}
                maxInputLength={8000}
                onChange={(e) => {
                    handleTextFieldEdition(e);
                }}
                error={false}
                errorMessage="error"
            />
        </div>
    );
};

const UploadContacts = (props: UploadContactsProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { upload, encoding, setEncoding, clearUploadedFileData } = props;

    const handleUploadFile = (File: File[]): void => {
        dispatch(UploadFiles(File[0], 1));
    };

    const handleRejectFile = (files: File[]): void => {
        const isAnyFileOverTheLimit = files?.some((file) => file.size > MAX_LIST_UPLOAD_SIZE);

        if (isAnyFileOverTheLimit) {
            dispatch(
                ShowToast({
                    message: t('uploadlistmaxedsize'),
                    severity: 'error',
                    duration: 8000,
                })
            );
        }
    };

    return (
        <div>
            {!upload.uploading && !upload.fileuploaded && (
                <div>
                    <DropZone
                        onDropAccepted={(files) => handleUploadFile(files)}
                        onDropRejected={(files) => handleRejectFile(files)}
                        acceptMimeType={mimeTypes}
                        linkText={t('uploadfile')}
                        textAfterLink={t('ordragdrop')}
                        description=".zip, .csv, .json, .xml"
                        maxSize={MAX_LIST_UPLOAD_SIZE}
                    />
                </div>
            )}
            {upload.uploading && (
                <div>
                    <UploadProgress
                        percentage={upload.progress}
                        textPrimary={t('weareuploadingyourdata')}
                        textSecondary={t('pleasedonotclosethisview')}
                    />
                </div>
            )}
            {upload.fileuploaded && (
                <div>
                    <div>
                        <UploadedFile
                            onDelete={(): void => {
                                clearUploadedFileData();
                            }}
                            filename={upload?.fileuploaded.FileName}
                        />
                    </div>
                </div>
            )}
            {!upload.uploading && (
                <FileFormat>
                    <SimplePopover
                        id="encoding-msg"
                        listItemWidth="400px"
                        options={encodingArray}
                        type="input"
                        label={t('fileencoding')}
                        maxHeight={400}
                        onItemClick={(_e, obj): void => {
                            setEncoding(obj.value as string);
                        }}
                        value={encoding}
                    />
                    <span>{t('encodingmsg')}</span>
                </FileFormat>
            )}
        </div>
    );
};

const ButtonFooterC = (props: AddContactButtonFooterComponentProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const upload = useSelector(getUploadStore);
    const customFieldsStore = useSelector(getCustomFieldsStore);
    const customFields = customFieldsStore.collection;
    const { encoding, sourceType, contactsPasted, setRecognizedEmails, setCurrentStep, resetUploadFlow } = props;

    const handleGetContactPattern = (): void => {
        dispatch(
            GetContactsPatternStart({
                fileName: upload.fileuploaded.FileName,
                encodingName: encoding || 'utf-8',
                errorModal: {
                    title: t('invalidfile'),
                    body: <WrongFileModal />,
                    cancelLabel: t('close'),
                    onClickCancel: (): void => {
                        setCurrentStep(STEPS.SOURCE_SELECTION);
                    },
                },
            })
        );
    };

    const handleGetContactsFromField = (): void => {
        const validEmails = filter(contactsPasted, (entry): boolean => isEmail(entry.email));
        setRecognizedEmails(validEmails.length);
    };

    return (
        <ButtonFooter>
            <TertiaryButton
                id="back-button"
                text={t('back')}
                onClick={(): void => resetUploadFlow(STEPS.SOURCE_SELECTION)}
            />
            <PrimaryButton
                id="continue-button"
                disabled={
                    (!upload?.fileuploaded || !customFields) &&
                    (!contactsPasted.length || (contactsPasted.length === 1 && !isEmail(contactsPasted[0].email)))
                }
                text={t('continue')}
                onClick={(): void => {
                    if (sourceType === 'upload') {
                        handleGetContactPattern();
                    } else {
                        handleGetContactsFromField();
                    }
                    setCurrentStep(STEPS.ADDING_AND_FIELDS_REVIEW);
                }}
            />
        </ButtonFooter>
    );
};

export const AddContacts = (props: AddContactsProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const account = useSelector(getAccountStore);
    const upload = useSelector(getUploadStore);
    const { mailchimp } = useSelector(getIntegrationsState);
    const [mailchimpListId, setMailchimpListId] = useState<string>();
    const stepChangedAfterMailchimpUpload = useRef<boolean>(false);
    const {
        encoding,
        setEncoding,
        sourceType,
        contactsPasted,
        setContactsPasted,
        setRecognizedEmails,
        setCurrentStep,
        clearUploadedFileData,
        resetUploadFlow,
    } = props;

    useEffect((): void => {
        if (!stepChangedAfterMailchimpUpload.current && mailchimp.listMembers.isLoaded) {
            stepChangedAfterMailchimpUpload.current = true;
            setCurrentStep(STEPS.ADDING_AND_FIELDS_REVIEW);
        }
    }, [mailchimp.listMembers]);

    if (sourceType === 'mailchimp') {
        const isMailchimpDataNotReady = mailchimp?.connection?.tempCode && !mailchimp.lists.isLoaded;
        if (!mailchimp?.connection?.tempCode || isMailchimpDataNotReady) {
            return (
                <div>
                    <Typography variant="h2">{t('mailchimptoelastic')}</Typography>
                    <Typography variant="body1" bodyVariant="onPaper">
                        {t('importallsubscribersfrommailchimp')}
                    </Typography>
                    <ButtonFooter>
                        <TertiaryButton
                            id="back-button"
                            text={t('back')}
                            onClick={(): void => resetUploadFlow(STEPS.SOURCE_SELECTION)}
                        />
                        <PrimaryButton
                            id="continue-button"
                            text={t('connectmailchimpaccount')}
                            disableSentenceCase
                            onClick={(): void => {
                                if (!MAILCHIMP_CLIENT_ID) {
                                    console.log('Missing Client ID.');
                                    return;
                                }
                                sendEventToGTM('MailchimpImport - Connect Mailchimp account');
                                window.location.href = `http://login.mailchimp.com/oauth2/authorize?response_type=code&client_id=${MAILCHIMP_CLIENT_ID}`;
                            }}
                            disabled={isMailchimpDataNotReady}
                            isLoading={isMailchimpDataNotReady}
                        />
                    </ButtonFooter>
                </div>
            );
        }
        const mailchimpListValue = mailchimpListId ?? mailchimp.lists.data?.[0]?.id ?? '';
        const membersIsLoading = mailchimp.listMembers.isLoading;
        return (
            <div>
                <Typography variant="body1" bodyVariant="onPaper">
                    {t('selectwhichmailchimplisttoimport')}
                </Typography>
                <Radio
                    id="mailchimp-lists-radios"
                    name="mailchimp-import-list"
                    required={true}
                    value={mailchimpListValue}
                    onChange={(e): void => {
                        setMailchimpListId((e.target as HTMLInputElement).value);
                    }}
                    radios={
                        mailchimp.lists?.data?.map((list) => ({
                            id: list?.id,
                            label: `${list?.name} (${list?.stats?.member_count} ${t('subscribers')})`,
                            value: list?.id,
                            disableSentenceCase: true,
                        })) ?? []
                    }
                    labelPlacement="end"
                />
                <ButtonFooter>
                    <TertiaryButton
                        id="back-button"
                        text={t('back')}
                        onClick={(): void => resetUploadFlow(STEPS.SOURCE_SELECTION)}
                    />
                    <PrimaryButton
                        id="continue-button"
                        text={membersIsLoading ? `${mailchimp.listMembers.progressPercent}%` : t('continue')}
                        disabled={!mailchimpListValue || membersIsLoading}
                        onClick={(): void => {
                            sendEventToGTM('MailchimpImport - continue import');
                            stepChangedAfterMailchimpUpload.current = false;
                            dispatch(GetMailchimpListMembers({ listId: mailchimpListValue }));
                        }}
                    />
                </ButtonFooter>
            </div>
        );
    }

    const alertText = (): JSX.Element => (
        <Typography variant="body1" bodyVariant="onPaper">
            {t('uploadcontactsfilesinfo')}
            {!isReseller(account) && (
                <span>
                    {`${t('howpreparefile')} `}
                    {
                        <Link href={links.contactUpload} rel="noreferrer" target="_blank">
                            {t('here')}.
                        </Link>
                    }
                </span>
            )}
        </Typography>
    );

    return (
        <div>
            <InfoDiv>
                <Alert body={alertText()} severity="info" />
            </InfoDiv>
            {sourceType === 'upload' ? (
                <UploadContacts
                    upload={upload}
                    encoding={encoding}
                    setEncoding={setEncoding}
                    clearUploadedFileData={clearUploadedFileData}
                />
            ) : (
                <TextField setContactsPasted={setContactsPasted} />
            )}
            <ButtonFooterC
                encoding={encoding}
                sourceType={sourceType}
                contactsPasted={contactsPasted}
                setRecognizedEmails={setRecognizedEmails}
                setCurrentStep={setCurrentStep}
                resetUploadFlow={resetUploadFlow}
            />
        </div>
    );
};
